import React, { useState, useEffect } from "react";
import { graphcms, QUERY_EVENT_BY_ID } from "../Graphql/Queries";
import { useParams, Link } from "react-router-dom";
import { DateTime } from "luxon";
import day1Flyer from "../img/Day_1.jpg";
import day2Flyer from "../img/Day_2.jpg";
import day3Flyer from "../img/Day_3.jpg";
import { Helmet } from "react-helmet-async";
import { Skeleton } from "@/components/ui/skeleton";

function Event() {
  const { eventID } = useParams();

  const aigniteFlyers = [day1Flyer, day2Flyer, day3Flyer];

  const [event, setEvent] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [speakerDetails, setSpeakerDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    graphcms.request(QUERY_EVENT_BY_ID, { eventID }).then((res) => {
      setEvent(res.events[0]);
      setSpeakers(res.events[0].speakers);
      setLoading(false);
    });
  }, [eventID]);

  const formatted = event?.eventDateTime
    ? DateTime.fromISO(event?.eventDateTime).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : "";

  const isEventPast = event?.eventDateTime
    ? DateTime.fromISO(event?.eventDateTime) < DateTime.now()
    : false;

  if (loading) {
    return (
      <div className="mt-10 mb-20 container flex flex-col gap-20 w-full sm:w-11/12 md:w-10/12 lg:w-11/12 mx-auto">
        <div className="w-full p-4 rounded-2xl shadow-lg grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="w-full h-96 rounded-2xl" />
          <div className="flex flex-col space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="mt-8 space-y-4">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-6 w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${event?.caption} - IEEE CIS SBC UoJ`}</title>
        <meta name="description" content={event?.description} />
        <link rel="canonical" href={`https://cis.jfn.ac.lk/event/${event?.id}`} />
      </Helmet>
      <div className="mt-10 mb-20 container flex flex-col gap-20 w-full sm:w-11/12 md:w-10/12 lg:w-11/12 mx-auto">
        <div className="w-full p-1 rounded-2xl shadow-[0_0_25px_rgba(0,0,0,0.15)] grid grid-rows-[35%_65%] grid-cols-none lg:grid-cols-[45%_55%] xl:grid-cols-[35%_65%] lg:grid-rows-none">
          <div className="w-full h-full flex items-center justify-center">
            <img
              src={event?.flyerImage?.url}
              alt={`${event?.caption} Event Flyer`}
              className="p-1 w-full h-auto rounded-2xl"
            />
          </div>

          <div className="w-full px-5 lg:pl-7 py-1 rounded-b-2xl flex flex-col justify-around text-center lg:text-left">
            <div className="mt-5">
              <h2 className="text-xl lg:text-2xl xl:text-3xl xl:font-extrabold font-bold mb-3 uppercase">
                {event?.caption}
              </h2>
              <p className="text-black indent-20 mb-4 lg:text-sm xl:text-base">
                {event?.description}
              </p>
            </div>
            <div className="w-full flex flex-col justify-around">
              <div className="flex mb-3">
                <h5 className="font-bold mr-12 lg:text-base">Speaker:</h5>
                <div className="lg:text-sm 2xl:text-base">
                  {speakers.length === 1 ? (
                    <div>
                      <p className="font-medium">{`${speakers[0]?.title}. ${speakers[0]?.name}`}</p>
                      <p>{speakers[0]?.occupation}</p>
                    </div>
                  ) : (
                    <p className="font-normal tracking-wide">{`There is ${speakers.length} Speakers. Please click More Details.`}</p>
                  )}
                  <a
                    href="#speakerDetails"
                    className=" text-sky-500 text-sm hover:underline hover:text-sky-700"
                    onClick={() => {
                      speakerDetails
                        ? setSpeakerDetails(false)
                        : setSpeakerDetails(true);
                    }}>
                    More details
                  </a>
                </div>
              </div>
              <div className="flex mb-3 items-center">
                <h5 className="font-bold mr-3 lg:text-base">Date & Time:</h5>
                <div>
                  <p className="lg:text-sm 2xl:text-base font-normal">
                    {formatted}
                  </p>
                </div>
              </div>
              <div className="flex mb-3 items-center">
                <h5 className="font-bold mr-16 lg:text-base">Venue:</h5>
                <div>
                  <p className="lg:text-sm 2xl:text-base font-normal">
                    {event?.venue}
                  </p>
                </div>
              </div>
              {event?.zoomLink !== null && (
                <div className="flex mb-3 items-center">
                  <h5 className="font-bold mr-7 lg:text-base">Zoom Link:</h5>
                  <a
                    href={event?.zoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-500 hover:text-sky-700 underline lg:text-sm 2xl:text-base font-normal">
                    Open the Link
                  </a>
                </div>
              )}
            </div>
            {event.registrationRequiredOrNot && (
              <Link
                to={event.registrationLink}
                className="self-center lg:self-end mt-5 lg:mb-5">
                <button className="px-8 py-3 bg-sky-600 text-white rounded-xl transition hover:bg-sky-700 lg:text-base ">
                  {isEventPast ? "Read more" : "Register"}
                </button>
              </Link>
            )}
          </div>
        </div>

        <div
          id="speakerDetails"
          className={`${
            speakerDetails ? "flex flex-wrap justify-center" : "hidden"
          } w-full h-fit sm:mt-10 gap-5`}>
          {speakers.map((speaker) => (
            <div
              className={`rounded-2xl shadow-[0_0_25px_rgba(0,0,0,0.15)] py-6 ${
                speaker.bio
                  ? "w-full grid auto-rows-auto lg:w-11/12 lg:grid-rows-none lg:grid-cols-[35%_65%]"
                  : "w-80"
              }`}>
              <div className="w-full rounded-l-2xl border-r-2 py-5 text-center flex flex-col items-center justify-center">
                {/* <h2 className="text-2xl font-bold my-5 md:mt-10">Speaker:</h2> */}
                <img
                  src={speaker.speakerImage?.url}
                  alt={`${speaker.title}. ${speaker.name}`}
                  className="w-2/3 h-auto rounded-xl border-8 p-1 border-sky-600 "
                />
                <p className="font-bold text-xl text-center mt-5">{`${speaker.title}. ${speaker.name}`}</p>
                <p className="text-md text-center px-5 lg:text-base">
                  {speaker.occupation}
                </p>
              </div>
              {speaker.bio && (
                <p className="py-5 px-6 indent-20 lg:mt-0 lg:text-base ">
                  {speaker.bio}
                </p>
              )}
            </div>
          ))}
        </div>

        {eventID === "clio9dc0j0tth0bo7e5s38c98" && (
          <div className="w-full sm:w-10/12 md:w-9/12 lg:w-auto h-auto mx-auto">
            {aigniteFlyers.map((flyer, index) => (
              <img
                src={flyer}
                alt={`Summer School Day 1 Flyer`}
                className="rounded-2xl w-full h-auto my-[50px]"
                key={index}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default Event;
